import { useState, useEffect, useCallback } from "react";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "@/lib/api/user";
import type { NotificationPreferences } from "@/types/user";

export interface UsePreferencesReturn {
  preferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  updatePreferences: (
    data: Partial<NotificationPreferences>
  ) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function usePreferences(): UsePreferencesReturn {
  const [preferences, setPreferences] =
    useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getNotificationPreferences();
      if (response.success && response.data) {
        setPreferences(response.data);
      } else {
        setError(response.message ?? "Failed to load preferences");
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreferences = useCallback(
    async (data: Partial<NotificationPreferences>): Promise<boolean> => {
      try {
        setSaving(true);
        const response = await updateNotificationPreferences(data);
        if (response.success && response.data) {
          setPreferences(response.data);
          return true;
        }
        setError(response.message ?? "Failed to update preferences");
        return false;
      } catch {
        setError("An unexpected error occurred");
        return false;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  return {
    preferences,
    loading,
    error,
    saving,
    updatePreferences,
    refetch: fetchPreferences,
  };
}
